import React, { useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (signatureDataUrl: string, title?: string) => void;
  title?: string;
  description?: string;
}

export function SignatureModal({
  isOpen,
  onClose,
  onSave,
  title = "Sign Document",
  description = "Please provide your signature below.",
}: SignatureModalProps) {
  const [activeTab, setActiveTab] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedFont, setSelectedFont] = useState("'Caveat', cursive");
  const [signerTitle, setSignerTitle] = useState("");
  const sigCanvas = useRef<SignatureCanvas>(null);

  // Create an invisible canvas to generate typed signature image
  const hiddenCanvasRef = useRef<HTMLCanvasElement>(null);

  const clearSignature = () => {
    if (activeTab === "draw" && sigCanvas.current) {
      sigCanvas.current.clear();
    } else {
      setTypedSignature("");
    }
  };

  const handleSave = () => {
    if (activeTab === "draw") {
      if (sigCanvas.current?.isEmpty()) {
        alert("Please provide a signature first.");
        return;
      }
      const dataUrl = sigCanvas.current?.getTrimmedCanvas().toDataURL("image/png");
      if (dataUrl) onSave(dataUrl, signerTitle);
    } else if (activeTab === "type") {
      if (!typedSignature.trim()) {
        alert("Please type your name to sign.");
        return;
      }

      const canvas = hiddenCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Draw the typed text to canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.font = `48px ${selectedFont}`;
      ctx.fillStyle = "black";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

      const dataUrl = canvas.toDataURL("image/png");
      onSave(dataUrl, signerTitle);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="draw" onValueChange={setActiveTab} className="mt-4 w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="draw">Draw Signature</TabsTrigger>
            <TabsTrigger value="type">Type Signature</TabsTrigger>
          </TabsList>

          <div className="mt-4 mb-2">
            <label className="text-sm font-medium text-gray-700">Your Title (Optional)</label>
            <Input
              type="text"
              placeholder="e.g. CEO, Manager"
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
              className="mt-1"
            />
          </div>

          <TabsContent value="draw" className="mt-4">
            <div className="rounded-md border bg-white">
              <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{ className: "w-full h-48 rounded-md bg-white" }}
              />
            </div>
          </TabsContent>

          <TabsContent value="type" className="mt-4 space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Type your full name"
                value={typedSignature}
                onChange={(e) => setTypedSignature(e.target.value)}
                className="flex-1 text-lg"
              />
              <Select value={selectedFont} onValueChange={setSelectedFont}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select Font" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="'Caveat', cursive">
                    <span style={{ fontFamily: "'Caveat', cursive", fontSize: "1.4rem" }}>
                      Signature
                    </span>
                  </SelectItem>
                  <SelectItem value="'Dancing Script', cursive">
                    <span style={{ fontFamily: "'Dancing Script', cursive", fontSize: "1.4rem" }}>
                      Signature
                    </span>
                  </SelectItem>
                  <SelectItem value="'Satisfy', cursive">
                    <span style={{ fontFamily: "'Satisfy', cursive", fontSize: "1.4rem" }}>
                      Signature
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex h-48 items-center justify-center overflow-hidden rounded-md border bg-white">
              {typedSignature ? (
                <span className="text-5xl text-black" style={{ fontFamily: selectedFont }}>
                  {typedSignature}
                </span>
              ) : (
                <span className="text-gray-400">Preview will appear here</span>
              )}
            </div>
            {/* Hidden canvas for generating the image */}
            <canvas ref={hiddenCanvasRef} width={600} height={200} className="hidden" />
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6 flex w-full sm:justify-between">
          <Button variant="outline" onClick={clearSignature}>
            Clear
          </Button>
          <div className="space-x-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Sign & Save</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
